import React, { useState, useEffect } from 'react';
import './SetForm.css';
import { getCalendarioDetalle } from '../services/jornadasService';
import { proponerResultado, crearPartido } from '../services/partidosService';
import { getParticipantesReal } from '../services/participantesService';
import { useTemporada } from '../context/TemporadaContext';

const MODALIDADES = [
  'DOBLES FEMENINO', 'DOBLES MASCULINO', 'DOBLES MIXTO',
  'INDIVIDUAL FEMENINO', 'INDIVIDUAL MASCULINO',
];

// Lo normal es proponer resultado de un partido PENDIENTE que ya existe en
// el calendario generado por round robin, así que primero se buscan los
// partidos pendientes de una jornada y luego se elige uno. Si el partido no
// está en el calendario (amistoso, recuperación...), "Crear partido nuevo"
// lo inserta suelto — aditivo, no toca el resto del calendario — y lo deja
// seleccionado para introducirle el resultado a continuación.
function SetForm() {
  const { temporada } = useTemporada();
  const [modalidad, setModalidad] = useState('');
  const [jornada, setJornada] = useState('');
  const [pendientes, setPendientes] = useState(null);
  const [conSetsGuardados, setConSetsGuardados] = useState(0);
  const [partidoId, setPartidoId] = useState('');
  const [scoresLocal, setScoresLocal] = useState(['', '', '']);
  const [scoresVisitante, setScoresVisitante] = useState(['', '', '']);
  const [feedback, setFeedback] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [creandoForm, setCreandoForm] = useState(false);
  const [equipos, setEquipos] = useState(null);
  const [equipoLocal, setEquipoLocal] = useState('');
  const [equipoVisitante, setEquipoVisitante] = useState('');
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    setPendientes(null);
    setPartidoId('');
    setFeedback(null);
    setCreandoForm(false);
    setEquipos(null);
    setEquipoLocal('');
    setEquipoVisitante('');
  }, [modalidad, jornada, temporada]);

  const buscarPartidos = async () => {
    if (!temporada || !modalidad || !jornada) return;
    setBuscando(true);
    setFeedback(null);
    try {
      const partidos = await getCalendarioDetalle(temporada, { modalidad, jornada: Number(jornada) });
      const sinResultado = partidos.filter(p => p.estado === 'PENDIENTE');
      // Con sets ya guardados desde Actas el partido sigue PENDIENTE, pero
      // proponerlo desde aquí duplicaría los sets: se confirma en Actas.
      setPendientes(sinResultado.filter(p => !p.sets?.length));
      setConSetsGuardados(sinResultado.filter(p => p.sets?.length).length);
    } catch {
      setFeedback({ tipo: 'error', msg: 'No se pudieron cargar los partidos. ¿Está el servidor en marcha?' });
    } finally {
      setBuscando(false);
    }
  };

  const toggleCrear = async () => {
    if (creandoForm) {
      setCreandoForm(false);
      return;
    }
    setFeedback(null);
    if (!equipos) {
      try {
        setEquipos(await getParticipantesReal(temporada, modalidad));
      } catch {
        setFeedback({ tipo: 'error', msg: 'No se pudieron cargar los equipos de esa modalidad.' });
        return;
      }
    }
    setCreandoForm(true);
  };

  const crearPartidoNuevo = async () => {
    if (!equipoLocal || !equipoVisitante) {
      setFeedback({ tipo: 'error', msg: 'Elige el equipo local y el visitante.' });
      return;
    }
    if (equipoLocal === equipoVisitante) {
      setFeedback({ tipo: 'error', msg: 'El equipo local y el visitante no pueden ser el mismo.' });
      return;
    }

    setCreando(true);
    setFeedback(null);
    try {
      const creado = await crearPartido({
        temporadaId: temporada,
        modalidadId: equipos[0].modalidad_id,
        jornada: Number(jornada),
        localParticipanteId: Number(equipoLocal),
        visitanteParticipanteId: Number(equipoVisitante),
      });
      const nombreDe = (id) => equipos.find(eq => String(eq.participante_id) === id)?.nombre_mostrar ?? '';
      const nuevo = {
        id: creado.id,
        estado: 'PENDIENTE',
        local_nombre: nombreDe(equipoLocal),
        visitante_nombre: nombreDe(equipoVisitante),
      };
      setPendientes(prev => [...(prev ?? []), nuevo]);
      setPartidoId(String(nuevo.id));
      setCreandoForm(false);
      setEquipoLocal('');
      setEquipoVisitante('');
      setFeedback({ tipo: 'ok', msg: 'Partido creado. Ya puedes introducir el resultado.' });
    } catch (err) {
      const mensaje = err.codigo === 'SIN_PERMISO'
        ? 'No tienes permiso para crear partidos.'
        : err.codigo === 'NO_AUTENTICADO'
          ? 'Tienes que iniciar sesión para crear un partido.'
          : err.message || 'Error al crear el partido.';
      setFeedback({ tipo: 'error', msg: mensaje });
    } finally {
      setCreando(false);
    }
  };

  const cambiarScore = (esLocal, index, valor) => {
    const setter = esLocal ? setScoresLocal : setScoresVisitante;
    setter(prev => prev.map((v, i) => i === index ? valor : v));
  };

  const partido = pendientes?.find(p => String(p.id) === String(partidoId));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partido) {
      setFeedback({ tipo: 'error', msg: 'Selecciona un partido pendiente.' });
      return;
    }

    const sets = scoresLocal
      .map((local, i) => ({ numero: i + 1, puntosLocal: Number(local), puntosVisitante: Number(scoresVisitante[i]) }))
      .filter(s => scoresLocal[s.numero - 1] !== '' && scoresVisitante[s.numero - 1] !== '');

    if (!sets.length) {
      setFeedback({ tipo: 'error', msg: 'Rellena al menos el primer set.' });
      return;
    }

    setEnviando(true);
    setFeedback(null);
    try {
      await proponerResultado(partido.id, sets);
      setFeedback({
        tipo: 'ok',
        msg: 'Resultado propuesto correctamente. Queda a la espera de que el comité lo confirme.',
      });
      setPendientes(prev => prev.filter(p => p.id !== partido.id));
      setPartidoId('');
      setScoresLocal(['', '', '']);
      setScoresVisitante(['', '', '']);
    } catch (err) {
      const mensaje = err.codigo === 'SIN_PERMISO'
        ? 'No puedes proponer el resultado de un partido en el que no juegas.'
        : err.codigo === 'NO_AUTENTICADO'
          ? 'Tienes que iniciar sesión para proponer un resultado.'
          : err.message || 'Error al guardar.';
      setFeedback({ tipo: 'error', msg: mensaje });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="set-form-page">
    <form className="set-form" onSubmit={handleSubmit}>
      {feedback && <p className={`feedback feedback-${feedback.tipo}`}>{feedback.msg}</p>}

      <div className="form-group">
        <label className="form-label">MODALIDAD:</label>
        <select className="form-input" value={modalidad} onChange={e => setModalidad(e.target.value)} required>
          <option value="">Seleccione modalidad</option>
          {MODALIDADES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">JORNADA:</label>
        <select className="form-input" value={jornada} onChange={e => setJornada(e.target.value)} required>
          <option value="">Nº JORNADA</option>
          {Array.from({ length: 14 }, (_, i) => (
            <option key={i} value={i + 1}>{`${i + 1}ª JORNADA`}</option>
          ))}
        </select>
      </div>

      <button type="button" className="form-button" onClick={buscarPartidos} disabled={!modalidad || !jornada || buscando}>
        {buscando ? 'Buscando...' : 'Buscar partidos pendientes'}
      </button>

      <button type="button" className="form-button" onClick={toggleCrear} disabled={!modalidad || !jornada}>
        {creandoForm ? 'Cancelar' : 'Crear partido nuevo'}
      </button>

      {creandoForm && equipos && equipos.length < 2 && (
        <p className="feedback feedback-error">No hay al menos dos equipos activos en esa modalidad y temporada.</p>
      )}

      {creandoForm && equipos && equipos.length >= 2 && (
        <>
          <div className="form-group">
            <label className="form-label">EQUIPO LOCAL:</label>
            <select className="form-input" value={equipoLocal} onChange={e => setEquipoLocal(e.target.value)}>
              <option value="">Seleccione equipo</option>
              {equipos.map(eq => (
                <option key={eq.participante_id} value={eq.participante_id}>{eq.nombre_mostrar}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">EQUIPO VISITANTE:</label>
            <select className="form-input" value={equipoVisitante} onChange={e => setEquipoVisitante(e.target.value)}>
              <option value="">Seleccione equipo</option>
              {equipos.map(eq => (
                <option key={eq.participante_id} value={eq.participante_id}>{eq.nombre_mostrar}</option>
              ))}
            </select>
          </div>

          <button type="button" className="form-button" onClick={crearPartidoNuevo} disabled={creando}>
            {creando ? 'Creando...' : 'Crear partido'}
          </button>
        </>
      )}

      {pendientes && pendientes.length === 0 && (
        <p className="feedback feedback-error">No hay partidos pendientes en esa jornada y modalidad.</p>
      )}

      {pendientes && conSetsGuardados > 0 && (
        <p className="feedback">
          {conSetsGuardados === 1 ? 'Hay 1 partido' : `Hay ${conSetsGuardados} partidos`} con
          sets ya guardados que no aparece{conSetsGuardados === 1 ? '' : 'n'} aquí: confírma
          {conSetsGuardados === 1 ? 'lo' : 'los'} desde Actas.
        </p>
      )}

      {pendientes && pendientes.length > 0 && (
        <div className="form-group">
          <label className="form-label">PARTIDO:</label>
          <select className="form-input" value={partidoId} onChange={e => setPartidoId(e.target.value)} required>
            <option value="">Seleccione un partido</option>
            {pendientes.map(p => (
              <option key={p.id} value={p.id}>{p.local_nombre} vs {p.visitante_nombre}</option>
            ))}
          </select>
        </div>
      )}

      {partido && (
        <div className='sets'>
          <div className='header_local'>{partido.local_nombre}</div>
          <div className='header_vs'>VS</div>
          <div className='header_visitante'>{partido.visitante_nombre}</div>
          {scoresLocal.map((_, i) => (
            <React.Fragment key={i}>
              <div className='sets_local' style={{ gridRow: i + 2 }}>
                <input type="number" min={0} className="form-input" placeholder={`Set ${i + 1}`}
                  value={scoresLocal[i]} onChange={e => cambiarScore(true, i, e.target.value)} />
              </div>
              <div className='sets_visitante' style={{ gridRow: i + 2 }}>
                <input type="number" min={0} className="form-input" placeholder={`Set ${i + 1}`}
                  value={scoresVisitante[i]} onChange={e => cambiarScore(false, i, e.target.value)} />
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {partido && (
        <button type="submit" className="form-button" disabled={enviando}>
          {enviando ? 'Enviando...' : 'Proponer resultado'}
        </button>
      )}
    </form>
    </div>
  );
}

export default SetForm;
